<section class="signup-page">
  @if (loading()) {
    <div class="signup-provisioning-backdrop" aria-hidden="false">
      <div
        class="signup-provisioning-modal"
        role="dialog"
        aria-modal="true"
        aria-busy="true"
        aria-labelledby="signup-provisioning-message"
      >
        <div class="signup-provisioning-spinner" aria-hidden="true"></div>
        <p
          id="signup-provisioning-message"
          class="signup-provisioning-message"
          aria-live="polite"
        >
          We are creating your workspace. This will take just a few seconds.
        </p>
      </div>
    </div>
  } @else if (verifyingOnboardingToken()) {
    <div class="signup-provisioning-backdrop" aria-hidden="false">
      <div
        class="signup-provisioning-modal"
        role="dialog"
        aria-modal="true"
        aria-busy="true"
        aria-labelledby="signup-verify-token-message"
      >
        <div class="signup-provisioning-spinner" aria-hidden="true"></div>
        <p id="signup-verify-token-message" class="signup-provisioning-message" aria-live="polite">
          Checking your signup link…
        </p>
      </div>
    </div>
  }

  <header class="signup-topbar">
    <a routerLink="/" class="signup-brand-link" aria-label="LaunchPocket home">
      <img src="assets/branding/logo-navy-1024.png" alt="LaunchPocket" class="signup-brand-logo" />
    </a>
  </header>

  <div class="signup-body">
    @if (onboardingTokenFatalError(); as tokenErr) {
      <h1 class="signup-title">This signup link is not valid</h1>
      <article class="signup-card signup-token-error-card">
        <p class="signup-token-error-copy">{{ tokenErr }}</p>
        <p class="signup-token-error-hint">
          Ask your admin for a new merchant signup link, or
          <a routerLink="/login">return to login</a>.
        </p>
      </article>
    } @else {
      <h1 class="signup-title">We need a few more details from you</h1>

      <article class="signup-card">
        <form [formGroup]="signupForm" (ngSubmit)="submit()" class="signup-form">
        <div class="field-block">
          <label for="name" class="field-label">Full name</label>
          <input id="name" type="text" formControlName="name" placeholder="Alex Rivera" autocomplete="name" />
          @if (fieldError('name')) {
            <p class="field-error">{{ fieldError('name') }}</p>
          }
        </div>

        <div class="field-block">
          <label for="email" class="field-label">Work email</label>
          <input
            id="email"
            type="email"
            formControlName="email"
            placeholder="alex@company.com"
            [class.signup-email--locked]="isCustomAppsOnboardingSignup"
            autocomplete="email"
          />
        </div>

        <div class="field-block">
          <label for="companyName" class="field-label">Company name</label>
          <input id="companyName" type="text" formControlName="company_name" placeholder="Acme Inc" autocomplete="organization" />
          @if (fieldError('company_name')) {
            <p class="field-error">{{ fieldError('company_name') }}</p>
          }
        </div>

        <div class="field-block">
          <label for="brandName" class="field-label">Brand name</label>
          <input id="brandName" type="text" formControlName="brand_name" placeholder="Acme" autocomplete="organization-title" />
          @if (fieldError('brand_name')) {
            <p class="field-error">{{ fieldError('brand_name') }}</p>
          }
        </div>

        <div class="field-block">
          <label for="companySize" class="field-label">Company size</label>
          <select id="companySize" formControlName="company_size" class="field-select">
            <option value="">Select size</option>
            @for (opt of companySizeOptions; track opt.value) {
              <option [value]="opt.value">{{ opt.label }}</option>
            }
          </select>
          @if (fieldError('company_size')) {
            <p class="field-error">{{ fieldError('company_size') }}</p>
          }
        </div>

        <div class="field-row">
          <div class="field-block">
            <label for="country" class="field-label">Country</label>
            <app-country-select
              id="country"
              formControlName="country"
              selectClass="field-select"
              placeholder="Select a country"
            />
            @if (fieldError('country')) {
              <p class="field-error">{{ fieldError('country') }}</p>
            }
          </div>
          <div class="field-block">
            <label for="mobile" class="field-label">Mobile number</label>
            <input id="mobile" type="tel" formControlName="mobile" autocomplete="tel" />
            @if (fieldError('mobile')) {
              <p class="field-error">{{ fieldError('mobile') }}</p>
            }
          </div>
        </div>

        @if (requirePassword()) {
          <div class="field-block">
            <label for="password" class="field-label">Password</label>
            <div class="password-input-wrap">
              <input
                id="password"
                [type]="passwordVisible() ? 'text' : 'password'"
                formControlName="password"
                autocomplete="new-password"
              />
              <app-password-visibility-toggle
                [visible]="passwordVisible()"
                [disabled]="loading() || verifyingOnboardingToken()"
                (toggled)="togglePasswordVisibility()"
              />
            </div>
            @if (fieldError('password')) {
              <p class="field-error">{{ fieldError('password') }}</p>
            }
            <p class="signup-hint">This password will be used to log in to your email account.</p>
          </div>
        }

        @if (formError()) {
          <p class="error form-error-banner">{{ formError() }}</p>
        }

        <div class="signup-trust">
          <svg class="signup-trust-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path
              d="M12 2L4 6v6c0 5.55 3.84 10.74 8 12 4.16-1.26 8-6.45 8-12V6l-8-4z"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linejoin="round"
            />
            <path d="M9 12l2 2 4-4" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <div class="signup-trust-copy">
            <span class="signup-trust-line">No credit card required</span>
            <span class="signup-trust-em">Takes less than 60 seconds</span>
          </div>
        </div>

        <button
          type="submit"
          class="signup-submit"
          [disabled]="loading() || verifyingOnboardingToken() || onboardingTokenFatalError()"
        >
          <span>Get Started</span>
          <svg class="signup-submit-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path
              d="M13.5 6.75L19.5 12L13.5 17.25"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path d="M4.5 12H19.25" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>
        </form>
      </article>

      <p class="signup-consent">
        By signing up, you agree to our
        <a routerLink="/terms">Terms of Service</a>
        and
        <a routerLink="/privacy">Privacy Policy</a>.
      </p>
    }
  </div>

  <footer class="signup-footer">
    <div class="signup-footer__divider" aria-hidden="true"></div>
    <div class="signup-footer__inner">
      <span class="signup-footer__copy">{{ siteFooterCopyright }}</span>
      <nav class="signup-footer__nav" aria-label="Legal">
        <a routerLink="/privacy">Privacy Policy</a>
        <a routerLink="/terms">Terms of Service</a>
        <a routerLink="/contact">Contact Support</a>
      </nav>
    </div>
  </footer>
</section>
